import React from 'react'
import { Redirect, useParams, matchPath, useHistory } from 'react-router-dom'

import { useFlag } from 'constants/flags'
import { routes, versionedRoutes, RouteParams } from 'constants/routes'
import useUserProfile from 'modules/user/useUserProfile'
import useUserSignedIn from 'modules/user/useUserSignedIn'

import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import { PageCanvas } from 'modules/shares/components/PageCanvas'
import DocumentSidebarLayout from 'containers/DocumentSidebarLayout'
import PageCanvasViewHeader from './components/PageCanvasViewHeader'
import { useShareWorkspaceSidebar } from 'modules/shares/hooks/useShareWorkspaceSidebar'
import { ProjectsMenu } from 'modules/projects/components/ProjectsMenu'
import { useVersioning } from 'modules/versioning'

import {
  showWorkspaceSettings,
  canUserAdministerShares,
} from 'modules/workspace/utils'
import { PageCanvasSupportGate } from './components/PageCanvasSupportGate'
import { WebRendererContextProvider } from '../components/WebRenderer/utils/WebRendererContext'
import { WebRendererCameraContextProvider } from '../components/WebRenderer/utils/WebRendererCameraContext'

export default function PageCanvasView() {
  const { share } = useVersioning()

  const {
    shouldRenderWorkspaceSidebar,
    workspace,
    workspaces,
  } = useShareWorkspaceSidebar(share)

  return (
    <FeatureFlagGate>
      <WebRendererContextProvider>
        <WebRendererCameraContextProvider>
          <DocumentSidebarLayout
            header={renderHeader}
            sidebarLeft={
              shouldRenderWorkspaceSidebar ? (
                <ProjectsMenu
                  workspace={workspace!}
                  workspaces={workspaces!}
                  showWorkspaceSettings={showWorkspaceSettings(workspace)}
                  canUserAdministerShares={canUserAdministerShares(workspace)}
                />
              ) : undefined
            }
            sidebarRight={null}>
            <PageCanvasSupportGate>
              <PageCanvas />
            </PageCanvasSupportGate>
          </DocumentSidebarLayout>
        </WebRendererCameraContextProvider>
      </WebRendererContextProvider>
    </FeatureFlagGate>
  )
}

function renderHeader(headerProps: any) {
  return <PageCanvasViewHeader {...headerProps} />
}

type FeatureFlagGateProps = {
  children: React.ReactElement
}

/**
 * Check if the user has access to the Web Canvas MVP FF.
 * - If it does, render the children
 * - If not, redirect the user to the normal artboard list mode.
 *
 * Note: User that are not logged in will be considered as not having the FF.
 *
 * This wrapping component can be removed once we remove the `web-canvas-mvp` FF
 * https://github.com/sketch-hq/Cloud/issues/9839
 */
function FeatureFlagGate({ children }: FeatureFlagGateProps) {
  const { data: userProfileData } = useUserProfile(false)
  const isSignedIn = useUserSignedIn()
  const isFeatureFlagDataReady = !!userProfileData?.me.featureFlags
  const hasWebCanvasMvp = useFlag('web-canvas-mvp')
  const { versionShortId } = useVersioning()
  const { shareID, pageUUID } = useParams<
    RouteParams<'SHARE_PAGE_CANVAS_VIEW'>
  >()
  const { location } = useHistory()

  const isVersionedRoute = Boolean(
    matchPath(location.pathname, {
      path: versionedRoutes.SHARE_PAGE_CANVAS_VIEW.VERSION.template(),
      exact: true,
    })
  )

  const redirectUrl = isVersionedRoute
    ? versionedRoutes.SHARE_PAGE_VIEW.VERSION.create({
        shareID,
        pageUUID,
        versionShortId,
      })
    : routes.SHARE_PAGE_VIEW.create({ shareID, pageUUID })

  // When the share is private, the `ShareRoutes` ancestor component will make sure the user
  // never reach this code if not signed in. However, when the share is public, a signed-out
  // user can reach this code. Signed-out users can't have the FF flag on, therefore we redirect.
  if (!isSignedIn) {
    return <Redirect to={redirectUrl} />
  }

  // The user is signed in, let's wait for the user feature flags before taking action.
  if (!isFeatureFlagDataReady) {
    return <DynamicLoadingPage />
  }

  if (!hasWebCanvasMvp) {
    return <Redirect to={redirectUrl} />
  }
  return children
}
